import { Breadcrumbs } from "components/breadcrumbs";
import { Link } from "react-router-dom";
import Button from "components/button";
import { PlusIcon } from '@heroicons/react/solid'
import { Filter, FipixAccountCard, FipixAccountTable } from "./components";
import { useMemo } from "react";
import { useTable, useUpdate } from "@refinedev/core";
import toast from "react-hot-toast";
import Pagination from "components/pagination";
import { FullScreenLoading } from "components/fullScreenLoading";
import { isHaveActionPermission } from "utills/helpers";

export const ListFipixAccount: React.FC = () => {
  const {
    tableQueryResult,
    filters,
    setFilters,
  } = useTable<any>({
    resource: "fipix/accounts",
    syncWithLocation: true,
    pagination: {
      mode: "off",
    },
    meta: {
      resource: "data",
      offset: true
    },
  });


  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : [],
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      is_active: logicalFilters.find((item) => item.field === "is_active")?.value || "",
      organization_id: logicalFilters.find((item) => item.field === "organization_id")?.value || "",
      role_id: logicalFilters.find((item) => item.field === "role_id")?.value || "",
    };
  }, [filters]);

  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const _toggleActive = (id: string, isActive: boolean) => {
    if (!isHaveActionPermission("accounts", "fipix", "edit")) return;
    if (window.confirm(`Are you sure you want to ${isActive ? "deactivate" : "activate"} this Account?`)) {
      updateMutation({
        resource: "fipix/accounts",
        id,
        meta: {
          URLSuffix: `active/${!isActive}`,
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
        },
      });
    }
  };

  const _handlePageChanged = (page: number) => {
    const offset = (page - 1) * 10;

    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "offset");
    if (pageIndex < 0) {
      updatedFilters.push({field: "offset", operator: "eq", value: offset});
    } else {
      updatedFilters[pageIndex].value = offset;
    }

    setFilters(updatedFilters);
  }

  if (tableQueryResult.isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        {...(isHaveActionPermission("accounts", "fipix", "create") && {
          additionalLabel: (
            <Link to={"/fipix/accounts/create"} target="_blank">
              <Button>
                <span className="sr-only">Create Fipix account</span>
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">
                  Create Fipix account
                </span>
              </Button>
            </Link>
          ),
        })}
      />
      <div className="box-container">
        <Filter currentFilterValues={currentFilterValues} setFilters={setFilters} />
      </div>

      <div className="box-container">
        {!tableQueryResult.data?.data?.length &&
          !tableQueryResult.isFetching && (
            <div className="text-center">
              No data
            </div>
          )}
        {tableQueryResult?.data?.data && tableQueryResult?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <FipixAccountCard
                data={tableQueryResult?.data?.data}
                pageSize={tableQueryResult?.data?.pageSize}
                currentPage={tableQueryResult?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="hidden sm:block">
              <FipixAccountTable
                data={tableQueryResult?.data?.data}
                pageSize={tableQueryResult?.data?.pageSize}
                currentPage={tableQueryResult?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
          </>
        ) : <></>}
        {tableQueryResult?.data?.paging ? (
          <div className="mt-6">
            <Pagination
              currentPage={tableQueryResult?.data?.paging.current_page}
              totalPage={tableQueryResult?.data?.paging.total_page}
              total={{
                totalItems: tableQueryResult?.data?.data?.length || 0,
                total: tableQueryResult?.data?.paging.total_item
              }}
              onChange={_handlePageChanged}
            />
          </div>
        ) : <></>}
      </div>
      {(tableQueryResult.isFetching && tableQueryResult.isPreviousData) || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </div>
  );
}